import { useTranslation } from 'react-i18next';
import { ExtensionConceptSummary } from 'entities/tag';
import { resolveConceptLabel } from 'app/utils/tag-utils';
import { useTagDisplay } from 'app/state/contexts/TagContext';

interface AnchoringPaneProps {
  extensions: ExtensionConceptSummary[] | undefined;
  selectedNodeKey: string | null;
  onSelect: (conceptName: string, nodeKey?: string) => void;
}

export const AnchoringPane = ({ extensions, selectedNodeKey, onSelect }: AnchoringPaneProps) => {
  const { t } = useTranslation('tag');

  const { tagLanguage } = useTagDisplay();

  const anchored = (extensions ?? []).filter((e) => e.widerAnchor || (e.narrowerAnchors && e.narrowerAnchors.length > 0));

  if (anchored.length === 0) {
    return <div className="tree-overview__pane-empty">{t('treeOverview.empty.anchoring')}</div>;
  }

  const labelOf = (labels: Record<string, Record<string, string>>, fallback: string) =>
    resolveConceptLabel({ languages: labels, currentLang: tagLanguage }) || fallback;

  return (
    <ul className="tree-overview__anchor-list">
      {anchored.map((ext) => (
        <li className="tree-overview__anchor-item" key={ext.id}>
          <div className={`tree-overview__anchor-ext ${selectedNodeKey === ext.id ? 'is-selected' : ''}`} onClick={() => onSelect(ext.name, ext.id)}>
            {labelOf(ext.labels, ext.name)}
          </div>
          {ext.widerAnchor && (
            <div
              className={`tree-overview__anchor-rel ${selectedNodeKey === `${ext.id}>w` ? 'is-selected' : ''}`}
              onClick={() => onSelect(ext.widerAnchor.name, `${ext.id}>w`)}
            >
              <span className="tree-overview__anchor-arrow">↑</span>
              <span className="tree-overview__anchor-kind">{t('treeOverview.anchoring.wider')}</span>
              <span>{labelOf(ext.widerAnchor.labels, ext.widerAnchor.name)}</span>
            </div>
          )}
          {(ext.narrowerAnchors ?? []).map((narrower) => (
            <div
              className={`tree-overview__anchor-rel ${selectedNodeKey === `${ext.id}>n>${narrower.conceptId}` ? 'is-selected' : ''}`}
              key={narrower.conceptId}
              onClick={() => onSelect(narrower.name, `${ext.id}>n>${narrower.conceptId}`)}
            >
              <span className="tree-overview__anchor-arrow">↓</span>
              <span className="tree-overview__anchor-kind">{t('treeOverview.anchoring.narrower')}</span>
              <span>{labelOf(narrower.labels, narrower.name)}</span>
            </div>
          ))}
        </li>
      ))}
    </ul>
  );
};
